import { FC, Fragment, useEffect, useState } from 'react';
import i18n, { changeLanguage } from 'i18next';
import { setContext, setUser } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { Layout } from 'src/layout/layout';
import { AppPreloader } from 'src/components/app-preloader';
import { useGetActiveBooking, useGetUpcomingBooking } from 'src/features/bookings';
import { useGetCurrentUser, UserDataFields } from 'src/features/user';
import { NoBookingsPage } from 'src/pages/no-bookings.page';
import { useLoginSupabase } from 'src/features/auth/hooks/use-login-supabase';
import { initLiveChat } from 'src/services/live-chat';
import { initI18Next } from 'src/i18n/instance';
import { Onboarding } from 'src/features/onboarding';
import { NotificationPermissionDialog } from 'src/features/notifications';
import { GA4 } from 'src/lib/ga4';
import { SupabaseLoginForm } from 'src/features/auth/components/supabase-login-form';
import { CommonLawTenancyModal } from 'src/features/common-law-tenancy';

import { appConfig } from './app-config';

const preloader = <AppPreloader size="big" showLogo css={{ flex: 1 }} />;

const ONBOARDING_STORAGE_KEY = 'onboarding_complete';

export const Main: FC = () => {
    const isAuthorized = useLoginSupabase();
    const [searchParams] = useSearchParams();

    const [isBookingsInvalid, setIsBookingsInvalid] = useState(false);
    const [intInitialized, setIntInitialized] = useState(i18n.isInitialized);
    const [supabaseLoading, setSupabaseLoading] = useState(true);
    const [passedOnboarding, setPassedOnboarding] = useState(
        Boolean(localStorage.getItem(ONBOARDING_STORAGE_KEY))
    );

    const refreshCache =
        searchParams.get('common_law') === 'true' || searchParams.get('refresh') === 'true';

    const { isLoading: loadingBookings } = useGetActiveBooking({
        enabled: isAuthorized,

        onError() {
            setIsBookingsInvalid(true);
        },

        onSuccess(booking) {
            setContext('active_booking', booking);

            if (!booking) {
                setIsBookingsInvalid(true);
            }
        },
    });

    const { data: upcomingBooking } = useGetUpcomingBooking({
        enabled: isAuthorized,
        bypassCache: refreshCache,
    });

    const queryClient = useQueryClient();

    useEffect(() => {
        initI18Next(null, () => {
            setIntInitialized(true);
        });
    }, []);

    useEffect(() => {
        const mutationCache = queryClient.getMutationCache();

        const unsubscribe = mutationCache.subscribe((event) => {
            if (event.mutation?.options.mutationKey?.join('.') === 'auth.supabase.initialize') {
                switch (event.mutation.state.status) {
                    case 'idle':
                    case 'loading':
                        setSupabaseLoading(true);
                        break;
                    default:
                        setSupabaseLoading(false);
                }
            }
        });

        return unsubscribe;
    }, [queryClient]);

    const {
        data: user,
        isLoading: isUserLoading,
        error: userError,
    } = useGetCurrentUser({
        enabled: isAuthorized,

        onSettled(data) {
            if (data?.language) {
                changeLanguage(data.language);
            }
        },

        onSuccess(data) {
            // @ts-ignore allow nullable emails
            setUser(data);

            GA4.setUser(data[UserDataFields.Id]);
        },
    });

    useEffect(initLiveChat, []);

    const handleSkipOnboarding = () => {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
        setPassedOnboarding(true);
    };

    if (appConfig.supabase.enabled && supabaseLoading) {
        return preloader;
    }

    if (!isAuthorized || loadingBookings || isUserLoading || !intInitialized) {
        if (!isAuthorized && appConfig.supabase.enabled && intInitialized)
            return <SupabaseLoginForm />;

        return preloader;
    }

    if (isBookingsInvalid) {
        return <NoBookingsPage />;
    }

    if (userError || !user) {
        // There is nothing we can do. Provide clear message for Sentry
        throw new Error('User data fetch fail', { cause: userError });
    }

    if (
        upcomingBooking?.common_law_tenancy_required &&
        !upcomingBooking?.common_law_tenancy_signed
    ) {
        return <CommonLawTenancyModal bookingId={upcomingBooking.id} userId={user.id} />;
    }

    const flags = user.feature_flags;

    const showNotificationDialog =
        flags.notifications &&
        appConfig.firebase.messaging.enabled &&
        !(flags.onboarding && !passedOnboarding); // ignore onboarding if the feature is disabled

    return (
        <Fragment>
            <Layout />

            {flags.onboarding && (
                <Onboarding passedOnboarding={passedOnboarding} onSkip={handleSkipOnboarding} />
            )}

            {showNotificationDialog && <NotificationPermissionDialog />}
        </Fragment>
    );
};
